import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, FileText } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl } from "../../lib/admin";

export default function AdminManuals() {
  const [manuals, setManuals] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: "", brand_id: "", pdf_data: "" });

  const load = () =>
    Promise.all([api.get("/manuals"), api.get("/brands")]).then(([m, b]) => {
      setManuals(m.data);
      setBrands(b.data);
    });
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.pdf_data) return toast.error("Please select a PDF file");
    try {
      await api.post("/manuals", form);
      toast.success("Uploaded");
      setEditing(false);
      setForm({ title: "", brand_id: "", pdf_data: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    await api.delete(`/manuals/${m.id}`);
    toast.success("Deleted");
    load();
  };

  const onFile = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm({ ...form, pdf_data: url });
  };

  return (
    <div data-testid="admin-manuals-page">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Documents</div>
          <h1 className="font-display text-4xl tracking-tighter">User Manuals</h1>
        </div>
        <button onClick={() => setEditing(true)} disabled={brands.length === 0} className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50" data-testid="manual-upload-btn">
          <Plus size={14} /> Upload Manual
        </button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3 font-normal">Title</th>
              <th className="px-4 py-3 font-normal">Brand</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {manuals.map((m) => (
              <tr key={m.id} className="border-b border-white/5">
                <td className="px-4 py-3 font-medium flex items-center gap-2"><FileText size={14} className="text-zinc-500" /> {m.title}</td>
                <td className="px-4 py-3 text-zinc-400">{brands.find((b) => b.id === m.brand_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(m)} className="text-zinc-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {manuals.length === 0 && <tr><td colSpan={3} className="px-4 py-10 text-center text-zinc-500">No manuals yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0A0A0A] border border-white/10 p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl">Upload Manual</div>
              <button type="button" onClick={() => setEditing(false)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Title</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="adm-input" data-testid="manual-form-title" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Brand</label>
                <select required value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} className="adm-input">
                  <option value="">Select</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">PDF File</label>
                <input required type="file" accept="application/pdf" onChange={(e) => onFile(e.target.files?.[0])} className="text-xs text-zinc-400" data-testid="manual-form-file" />
                {form.pdf_data && <div className="text-xs text-emerald-400 mt-2">File loaded.</div>}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(false)} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5">Cancel</button>
              <button type="submit" className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]" data-testid="manual-form-save">Upload</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}
